
import { db, storageBucket } from '../services/firebase';
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  doc, 
  updateDoc, 
  deleteDoc,
  serverTimestamp,
  limit
} from "firebase/firestore";
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from "firebase/storage";
import { Journey, Session, KnowledgeDoc, StrategicPlan, JourneyAnswerSet } from '../types';

const LS_KEYS = {
  JOURNEYS: 'medinter_journeys',
  SESSIONS: 'medinter_sessions',
  KNOWLEDGE: 'medinter_knowledge',
  STRATEGIC_PLAN: 'medinter_strategic_plan',
  JOURNEY_ANSWERS: 'medinter_journey_answers'
};

let isCloudEnabled = true; 

const hasLocalStorage = typeof localStorage !== 'undefined';
const readLocal = (key: string, fallback: string = '') => (hasLocalStorage ? (localStorage.getItem(key) ?? fallback) : fallback);
const writeLocal = (key: string, value: string) => {
  if (hasLocalStorage) localStorage.setItem(key, value);
};

/**
 * Firestore does not allow 'undefined' values. 
 * This helper removes any keys with undefined values.
 */
const sanitize = (obj: any) => {
  const sanitized: any = {};
  Object.keys(obj).forEach(key => {
    if (obj[key] !== undefined) {
      sanitized[key] = obj[key];
    }
  });
  return sanitized;
};

const convertTimestamps = (data: any) => {
  const result = { ...data };
  for (const key in result) {
    if (result[key] && typeof result[key].toDate === 'function') {
      result[key] = result[key].toDate().toISOString();
    }
  }
  return result;
};

const isPermissionError = (error: any) => {
  return error.code === 'permission-denied' || 
         (error.message && (error.message.includes('permission-denied') || error.message.includes('insufficient permissions')));
};

export const storage = {
  async getJourneys(userId: string): Promise<Journey[]> {
    if (isCloudEnabled) {
      try {
        // Get user's own journeys
        const userJourneysQuery = query(collection(db, "journeys"), where("userId", "==", userId));
        const userJourneysSnapshot = await getDocs(userJourneysQuery);
        const userJourneys = userJourneysSnapshot.docs.map(doc => ({
          ...convertTimestamps(doc.data()),
          id: doc.id
        } as Journey));

        // Get public journeys from other users
        const publicJourneysQuery = query(collection(db, "journeys"), where("isPublic", "==", true));
        const publicJourneysSnapshot = await getDocs(publicJourneysQuery);
        const publicJourneys = publicJourneysSnapshot.docs
          .map(doc => ({
            ...convertTimestamps(doc.data()),
            id: doc.id
          } as Journey))
          .filter(journey => journey.userId !== userId); // Exclude user's own public journeys

        // Combine and sort by createdAt
        const allJourneys = [...userJourneys, ...publicJourneys]
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

        return allJourneys;
      } catch (e: any) {
        if (!isPermissionError(e)) console.error("getJourneys failure:", e);
      }
    }
    return JSON.parse(readLocal(LS_KEYS.JOURNEYS) || '[]').filter((j: any) => j.userId === userId);
  },

  async createJourney(journey: Journey): Promise<Journey> {
    if (isCloudEnabled) {
      try {
        const { id, ...dataToSave } = journey;
        const docRef = await addDoc(collection(db, "journeys"), sanitize({
          ...dataToSave,
          createdAt: serverTimestamp()
        }));
        return { ...journey, id: docRef.id, createdAt: new Date().toISOString() };
      } catch (e: any) {
        if (!isPermissionError(e)) console.error("createJourney failure:", e);
      }
    }
    const journeys = JSON.parse(readLocal(LS_KEYS.JOURNEYS) || '[]');
    const newJourney = { ...journey, id: journey.id || 'local_' + Math.random().toString(36).substr(2, 9), createdAt: new Date().toISOString() };
    writeLocal(LS_KEYS.JOURNEYS, JSON.stringify([newJourney, ...journeys]));
    return newJourney;
  },

  async updateJourneyQuestions(journeyId: string, questions: any[]): Promise<void> {
    if (isCloudEnabled && !journeyId.startsWith('local_')) {
      try {
        const journeyRef = doc(db, "journeys", journeyId);
        await updateDoc(journeyRef, { questions });
        return;
      } catch (e: any) {
        if (!isPermissionError(e)) console.error("updateJourneyQuestions failure:", e);
      }
    }
    const journeys = JSON.parse(readLocal(LS_KEYS.JOURNEYS) || '[]');
    const updated = journeys.map((j: any) => j.id === journeyId ? { ...j, questions } : j);
    writeLocal(LS_KEYS.JOURNEYS, JSON.stringify(updated));
  },

  async getSessions(userId: string): Promise<Session[]> {
    if (isCloudEnabled) {
      try {
        // Get user's own sessions
        const userSessionsQuery = query(collection(db, "sessions"), where("userId", "==", userId));
        const userSessionsSnapshot = await getDocs(userSessionsQuery);
        const userSessions = userSessionsSnapshot.docs.map(doc => ({
          ...convertTimestamps(doc.data()),
          id: doc.id
        } as Session));

        // Get public sessions from other users
        const publicSessionsQuery = query(collection(db, "sessions"), where("isPublic", "==", true));
        const publicSessionsSnapshot = await getDocs(publicSessionsQuery);
        const publicSessions = publicSessionsSnapshot.docs
          .map(doc => ({
            ...convertTimestamps(doc.data()),
            id: doc.id
          } as Session))
          .filter(session => session.userId !== userId); // Exclude user's own public sessions (already included above)

        // Combine and sort by startTime
        const allSessions = [...userSessions, ...publicSessions]
          .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

        return allSessions;
      } catch (e: any) {
        if (!isPermissionError(e)) console.error("getSessions failure:", e);
      }
    }
    return JSON.parse(readLocal(LS_KEYS.SESSIONS) || '[]').filter((s: any) => s.userId === userId);
  },

  async createSession(session: Session): Promise<Session> {
    if (isCloudEnabled) {
      try {
        const { id, ...dataToSave } = session;
        const docRef = await addDoc(collection(db, "sessions"), sanitize({
          ...dataToSave,
          startTime: serverTimestamp(),
          currentQuestionIdx: 0,
          activeFollowUp: null,
          isComplete: false
        }));
        return { ...session, id: docRef.id, startTime: new Date().toISOString(), currentQuestionIdx: 0, activeFollowUp: null };
      } catch (e: any) {
        if (!isPermissionError(e)) console.error("createSession failure:", e);
      }
    }
    const sessions = JSON.parse(readLocal(LS_KEYS.SESSIONS) || '[]');
    const newSession = { ...session, id: 'local_' + Math.random().toString(36).substr(2, 9), startTime: new Date().toISOString() };
    writeLocal(LS_KEYS.SESSIONS, JSON.stringify([newSession, ...sessions]));
    return newSession;
  },

  async updateSession(session: Session): Promise<void> {
    if (isCloudEnabled && session.id && !session.id.startsWith('local_')) {
      try {
        const sessionRef = doc(db, "sessions", session.id);
        const updateData: any = {
          attempts: session.attempts,
          isComplete: session.isComplete || false,
          overallSummary: session.overallSummary || "",
          currentQuestionIdx: session.currentQuestionIdx ?? 0,
          activeFollowUp: session.activeFollowUp ?? null
        };
        if (session.endTime) updateData.endTime = session.endTime;
        await updateDoc(sessionRef, sanitize(updateData));
        return;
      } catch (e: any) {
        if (!isPermissionError(e)) console.error("updateSession failure:", e);
      }
    }
    const sessions = JSON.parse(readLocal(LS_KEYS.SESSIONS) || '[]');
    const updated = sessions.map((s: any) => s.id === session.id ? session : s);
    writeLocal(LS_KEYS.SESSIONS, JSON.stringify(updated));
  },

  async getKnowledge(userId: string): Promise<KnowledgeDoc[]> {
    if (isCloudEnabled) {
      try {
        const q = query(collection(db, "knowledge"), where("userId", "==", userId));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ ...convertTimestamps(doc.data()), id: doc.id } as KnowledgeDoc));
      } catch (e: any) {
        if (!isPermissionError(e)) console.error("getKnowledge failure:", e);
      }
    }
    return JSON.parse(readLocal(LS_KEYS.KNOWLEDGE) || '[]');
  },

  async addKnowledgeDoc(docData: KnowledgeDoc, file?: File): Promise<KnowledgeDoc> {
    let finalFileUrl = docData.fileUrl || null;
    
    // Handle File upload to Storage if present
    if (isCloudEnabled && file) {
      try {
        const fileId = Math.random().toString(36).substr(2, 9);
        const storageRef = ref(storageBucket, `users/${docData.userId}/knowledge/${fileId}_${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        finalFileUrl = await getDownloadURL(snapshot.ref);
      } catch (e) {
        console.error("File upload failed:", e);
      }
    }

    if (isCloudEnabled) {
      try {
        const { id, ...data } = docData;
        const payload = sanitize({
          ...data,
          fileUrl: finalFileUrl,
          createdAt: serverTimestamp()
        });

        const docRef = await addDoc(collection(db, "knowledge"), payload);
        return { ...docData, id: docRef.id, fileUrl: finalFileUrl || undefined };
      } catch (e: any) {
        if (!isPermissionError(e)) console.error("addKnowledgeDoc failure:", e);
      }
    }
    
    const docs = JSON.parse(readLocal(LS_KEYS.KNOWLEDGE) || '[]');
    const newDoc = { ...docData, id: docData.id || 'local_' + Math.random().toString(36).substr(2, 9), fileUrl: finalFileUrl || undefined };
    writeLocal(LS_KEYS.KNOWLEDGE, JSON.stringify([newDoc, ...docs]));
    return newDoc;
  },

  async deleteKnowledgeDoc(docId: string, fileUrl?: string): Promise<void> {
    if (isCloudEnabled && !docId.startsWith('local_')) {
      try {
        // Delete from Firestore
        await deleteDoc(doc(db, "knowledge", docId));
        
        // Optionally delete from Storage if fileUrl is present
        if (fileUrl && fileUrl.includes("firebasestorage.googleapis.com")) {
          try {
             const storageRef = ref(storageBucket, fileUrl);
             await deleteObject(storageRef);
          } catch (se) {
            console.warn("Storage deletion failed (might already be gone):", se);
          }
        }
        return;
      } catch (e: any) {
        if (!isPermissionError(e)) console.error("deleteKnowledgeDoc failure:", e);
      }
    }
    const docs = JSON.parse(readLocal(LS_KEYS.KNOWLEDGE) || '[]');
    writeLocal(LS_KEYS.KNOWLEDGE, JSON.stringify(docs.filter((d: any) => d.id !== docId)));
  },

  async getStrategicPlan(userId: string): Promise<StrategicPlan | null> {
    if (isCloudEnabled) {
      try {
        const q = query(collection(db, "strategic_plans"), where("userId", "==", userId), limit(1));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const docData = querySnapshot.docs[0].data();
          return { ...convertTimestamps(docData) } as StrategicPlan;
        }
      } catch (e: any) {
        if (!isPermissionError(e)) console.error("getStrategicPlan failure:", e);
      }
    }
    const plan = readLocal(LS_KEYS.STRATEGIC_PLAN + "_" + userId);
    return plan ? JSON.parse(plan) : null;
  },

  async saveStrategicPlan(plan: StrategicPlan): Promise<void> {
    if (isCloudEnabled) {
      try {
        const q = query(collection(db, "strategic_plans"), where("userId", "==", plan.userId), limit(1));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const existingDocId = querySnapshot.docs[0].id;
          const docRef = doc(db, "strategic_plans", existingDocId);
          await updateDoc(docRef, sanitize({ 
            ...plan, 
            updatedAt: serverTimestamp() 
          }));
        } else {
          await addDoc(collection(db, "strategic_plans"), sanitize({ 
            ...plan, 
            userId: plan.userId,
            updatedAt: serverTimestamp() 
          }));
        }
        return;
      } catch (e: any) {
        if (!isPermissionError(e)) console.error("saveStrategicPlan failure:", e);
      }
    }
    writeLocal(LS_KEYS.STRATEGIC_PLAN + "_" + plan.userId, JSON.stringify(plan));
  },

  async getJourneyAnswerSets(userId: string, journeyId: string): Promise<JourneyAnswerSet[]> {
    if (isCloudEnabled) {
      try {
        const q = query(
          collection(db, "journey_answers"),
          where("userId", "==", userId),
          where("journeyId", "==", journeyId)
        );
        const querySnapshot = await getDocs(q);
        const sets = querySnapshot.docs.map(docSnap => ({
          ...(convertTimestamps(docSnap.data()) as JourneyAnswerSet),
          id: docSnap.id
        }));
        return sets.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
      } catch (e: any) {
        if (!isPermissionError(e)) console.error("getJourneyAnswerSets failure:", e);
      }
    }
    const key = `${LS_KEYS.JOURNEY_ANSWERS}_${userId}_${journeyId}`;
    const stored = readLocal(key);
    return stored ? JSON.parse(stored) : [];
  },

  async saveJourneyAnswers(userId: string, journeyId: string, answers: Record<string, string>): Promise<void> {
    if (isCloudEnabled) {
      try {
        await addDoc(collection(db, "journey_answers"), sanitize({
          userId,
          journeyId,
          answers,
          createdAt: serverTimestamp()
        }));
        return;
      } catch (e: any) {
        if (!isPermissionError(e)) console.error("saveJourneyAnswers failure:", e);
      }
    }
    const key = `${LS_KEYS.JOURNEY_ANSWERS}_${userId}_${journeyId}`;
    const existing = readLocal(key);
    const list = existing ? JSON.parse(existing) : [];
    list.unshift({ userId, journeyId, answers, createdAt: new Date().toISOString() });
    writeLocal(key, JSON.stringify(list));
  }
};
